import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";
import {
  Download, LogOut, Search, RefreshCw, Phone, Mail, X, MessageSquare,
  LayoutGrid, List as ListIcon, Send, Clock, User as UserIcon, Settings,
  Trash2,
} from "lucide-react";
import { toast } from "sonner";

const LOGO = "https://customer-assets.emergentagent.com/job_media-insights-26/artifacts/jyhdvnlr_logo-png-cuadrado.png";
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_OPTIONS = [
  { value: "nuevo",      label: "Nuevo",      color: "bg-[#8B1538] text-white", dot: "bg-[#8B1538]" },
  { value: "contactado", label: "Contactado", color: "bg-[#2E2638] text-white", dot: "bg-[#2E2638]" },
  { value: "en_tienda",  label: "En tienda",  color: "bg-[#E0A500] text-white", dot: "bg-[#E0A500]" },
  { value: "convertido", label: "Convertido", color: "bg-emerald-600 text-white", dot: "bg-emerald-600" },
  { value: "descartado", label: "Descartado", color: "bg-[#B5AFBE] text-white", dot: "bg-[#B5AFBE]" },
];

const SOURCE_BADGES = {
  parejas:     { label: "PAREJAS",    bg: "bg-[#8B1538] text-white" },
  deportistas: { label: "DEPORTE",    bg: "bg-emerald-700 text-white" },
  oficinistas: { label: "OFICINA",    bg: "bg-[#2E2638] text-white" },
  esfuerzo:    { label: "ESFUERZO",   bg: "bg-[#7A4B00] text-white" },
  dudas:       { label: "DUDAS",      bg: "bg-[#E0A500] text-white" },
  cheque:      null,
};

function formatDate(iso) {
  try {
    const d = new Date(iso);
    return d.toLocaleString("es-ES", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch { return iso; }
}

function timeAgo(iso) {
  try {
    const diff = (Date.now() - new Date(iso).getTime()) / 1000;
    if (diff < 60) return "ahora";
    if (diff < 3600) return `hace ${Math.floor(diff / 60)} min`;
    if (diff < 86400) return `hace ${Math.floor(diff / 3600)} h`;
    if (diff < 604800) return `hace ${Math.floor(diff / 86400)} d`;
    return new Date(iso).toLocaleDateString("es-ES", { day: "2-digit", month: "short" });
  } catch { return ""; }
}

function waLink(phone) {
  const clean = (phone || "").replace(/[^\d+]/g, "");
  const text = encodeURIComponent("Hola, te contacto desde CORE COLCHÓN sobre el cheque de 100€ que solicitaste.");
  return `https://wa.me/${clean.startsWith("+") ? clean.slice(1) : clean}?text=${text}`;
}

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState({ total: 0, today: 0, nuevos: 0 });
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [view, setView] = useState("pipeline"); // "pipeline" | "list"
  const [openLead, setOpenLead] = useState(null); // lead object opened in modal

  const load = async () => {
    setLoading(true);
    try {
      const [leadsRes, statsRes] = await Promise.all([
        axios.get(`${API}/admin/leads`),
        axios.get(`${API}/admin/stats`),
      ]);
      setLeads(leadsRes.data || []);
      setStats(statsRes.data || { total: 0, today: 0, nuevos: 0 });
    } catch {
      toast.error("No se pudieron cargar los leads.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const updateStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/admin/leads/${id}/status`, { status });
      setLeads((ls) => ls.map((l) => (l.id === id ? { ...l, status } : l)));
      toast.success("Estado actualizado");
    } catch {
      toast.error("Error al actualizar el estado");
    }
  };

  const downloadCSV = async () => {
    try {
      const res = await axios.get(`${API}/admin/leads/export`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `leads_corecolchon_${new Date().toISOString().slice(0,10)}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error("Error al exportar CSV");
    }
  };

  const filtered = useMemo(() => leads.filter((l) => {
    if (statusFilter !== "all" && l.status !== statusFilter) return false;
    if (!q) return true;
    const v = q.toLowerCase();
    return [l.name, l.email, l.phone, l.size_pref].filter(Boolean).some((f) => f.toLowerCase().includes(v));
  }), [leads, q, statusFilter]);

  const grouped = useMemo(() => {
    const out = {};
    STATUS_OPTIONS.forEach((s) => { out[s.value] = []; });
    filtered.forEach((l) => {
      const k = l.status || "nuevo";
      if (out[k]) out[k].push(l);
    });
    return out;
  }, [filtered]);

  return (
    <div className="min-h-screen bg-[#F9F8FA]">
      <header className="bg-white border-b border-[#E3DDE8] sticky top-0 z-20">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-10 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src={LOGO} alt="Core" className="h-9 w-9 object-contain"/>
            <div>
              <div className="font-display font-bold text-[#2E2638] leading-none">CORE COLCHÓN</div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-[#8B1538] font-bold">CRM Comercial</div>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <Link to="/admin/settings" data-testid="nav-settings" className="text-xs uppercase tracking-wider font-bold flex items-center gap-1.5 text-[#5C5366] hover:text-[#8B1538] transition-colors">
              <Settings size={14}/> Ajustes
            </Link>
            <span className="text-xs sm:text-sm text-[#5C5366] hidden sm:block">{user?.email}</span>
            <button data-testid="admin-logout" onClick={logout} className="text-xs uppercase tracking-wider font-bold flex items-center gap-1.5 text-[#5C5366] hover:text-[#8B1538] transition-colors">
              <LogOut size={14}/> Salir
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-[1400px] mx-auto px-5 sm:px-10 py-8">
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mb-6">
          <StatCard label="Leads totales" value={stats.total} accent />
          <StatCard label="Hoy" value={stats.today} />
          <StatCard label="Sin contactar" value={stats.nuevos} />
        </div>

        <div className="bg-white border border-[#E3DDE8]">
          <div className="px-5 sm:px-7 py-4 border-b border-[#E3DDE8] flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-3 flex-wrap">
              <div className="relative">
                <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#5C5366]"/>
                <input
                  data-testid="leads-search"
                  type="search"
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="Buscar por nombre, email, teléfono…"
                  className="pl-9 pr-3 py-2 border border-[#E3DDE8] focus:border-[#8B1538] outline-none bg-transparent text-sm w-72"
                />
              </div>
              <select
                data-testid="leads-status-filter"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="px-3 py-2 border border-[#E3DDE8] focus:border-[#8B1538] outline-none bg-transparent text-sm"
              >
                <option value="all">Todos los estados</option>
                {STATUS_OPTIONS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
              </select>
            </div>
            <div className="flex items-center gap-2">
              <div className="flex border border-[#E3DDE8]">
                <button
                  data-testid="view-pipeline"
                  onClick={() => setView("pipeline")}
                  className={`px-3 py-2 text-sm flex items-center gap-1.5 transition-colors ${view === "pipeline" ? "bg-[#2E2638] text-white" : "bg-white text-[#5C5366] hover:text-[#8B1538]"}`}
                >
                  <LayoutGrid size={14}/> Pipeline
                </button>
                <button
                  data-testid="view-list"
                  onClick={() => setView("list")}
                  className={`px-3 py-2 text-sm flex items-center gap-1.5 transition-colors ${view === "list" ? "bg-[#2E2638] text-white" : "bg-white text-[#5C5366] hover:text-[#8B1538]"}`}
                >
                  <ListIcon size={14}/> Lista
                </button>
              </div>
              <button data-testid="leads-refresh" onClick={load} className="px-3 py-2 border border-[#E3DDE8] hover:border-[#8B1538] text-sm flex items-center gap-1.5 text-[#5C5366] hover:text-[#8B1538] transition-colors">
                <RefreshCw size={14}/> Recargar
              </button>
              <button data-testid="leads-export-csv" onClick={downloadCSV} className="px-4 py-2 bg-[#8B1538] hover:bg-[#6A0F2A] text-white text-sm font-bold uppercase tracking-wider flex items-center gap-2 transition-colors">
                <Download size={14}/> CSV
              </button>
            </div>
          </div>

          {loading ? (
            <div className="px-5 py-16 text-center text-[#5C5366]">Cargando leads…</div>
          ) : filtered.length === 0 ? (
            <div className="px-5 py-16 text-center text-[#5C5366]">No hay leads que coincidan.</div>
          ) : view === "pipeline" ? (
            <Pipeline grouped={grouped} onOpen={setOpenLead} onMove={updateStatus} />
          ) : (
            <LeadsTable leads={filtered} onOpen={setOpenLead} onUpdateStatus={updateStatus} />
          )}
        </div>
      </main>

      {openLead && (
        <LeadDetailModal
          leadId={openLead.id}
          onClose={() => setOpenLead(null)}
          onStatusChanged={(id, status) => setLeads((ls) => ls.map((l) => l.id === id ? { ...l, status } : l))}
          onDeleted={(id) => {
            setLeads((ls) => ls.filter((l) => l.id !== id));
            setOpenLead(null);
          }}
        />
      )}
    </div>
  );
}

function StatCard({ label, value, accent }) {
  return (
    <div className={`p-6 border ${accent ? "border-[#8B1538] bg-[#8B1538] text-white" : "border-[#E3DDE8] bg-white text-[#2E2638]"}`}>
      <div className={`text-[11px] uppercase tracking-[0.2em] font-bold ${accent ? "text-white/70" : "text-[#5C5366]"}`}>{label}</div>
      <div className="font-display text-4xl font-extrabold mt-1">{value}</div>
    </div>
  );
}

function Pipeline({ grouped, onOpen, onMove }) {
  const [dragId, setDragId] = useState(null);
  const [hoverCol, setHoverCol] = useState(null);

  const onDragStart = (id) => setDragId(id);
  const onDragOver = (e, col) => { e.preventDefault(); setHoverCol(col); };
  const onDrop = (col) => {
    if (dragId) onMove(dragId, col);
    setDragId(null);
    setHoverCol(null);
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-3 p-4 bg-[#F9F8FA]">
      {STATUS_OPTIONS.map((col) => (
        <div
          key={col.value}
          data-testid={`pipeline-col-${col.value}`}
          onDragOver={(e) => onDragOver(e, col.value)}
          onDrop={() => onDrop(col.value)}
          className={`bg-white border ${hoverCol === col.value ? "border-[#8B1538] ring-2 ring-[#8B1538]/20" : "border-[#E3DDE8]"} flex flex-col min-h-[400px]`}
        >
          <div className="px-3 py-2.5 border-b border-[#E3DDE8] flex items-center justify-between sticky top-0 bg-white">
            <div className="flex items-center gap-2">
              <span className={`w-2 h-2 rounded-full ${col.dot}`}/>
              <span className="text-[11px] uppercase tracking-wider font-bold text-[#2E2638]">{col.label}</span>
            </div>
            <span className="text-xs font-bold text-[#5C5366] bg-[#F9F8FA] px-2 py-0.5">{grouped[col.value].length}</span>
          </div>
          <div className="flex-1 p-2 space-y-2 overflow-y-auto max-h-[70vh]">
            {grouped[col.value].length === 0 ? (
              <div className="text-center text-xs text-[#B5AFBE] py-8">Sin leads</div>
            ) : grouped[col.value].map((l) => (
              <article
                key={l.id}
                data-testid={`pipeline-card-${l.id}`}
                draggable
                onDragStart={() => onDragStart(l.id)}
                onClick={() => onOpen(l)}
                className="bg-white border border-[#E3DDE8] hover:border-[#8B1538] hover:shadow-md p-3 cursor-grab active:cursor-grabbing transition-all"
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="font-bold text-sm text-[#2E2638] leading-tight flex-1">{l.name}</div>
                  {SOURCE_BADGES[l.source] && (
                    <span className={`text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 ${SOURCE_BADGES[l.source].bg}`}>
                      {SOURCE_BADGES[l.source].label}
                    </span>
                  )}
                </div>
                <div className="mt-1.5 text-xs text-[#5C5366] flex items-center gap-1">
                  <Phone size={11}/> {l.phone}
                </div>
                {l.size_pref && (
                  <div className="mt-1 text-[11px] text-[#B5AFBE]">{l.size_pref}</div>
                )}
                <div className="mt-2 flex items-center justify-between">
                  <span className="text-[10px] uppercase tracking-wider text-[#B5AFBE] flex items-center gap-1"><Clock size={10}/>{timeAgo(l.created_at)}</span>
                  <div className="flex gap-1.5">
                    <a onClick={(e)=>e.stopPropagation()} href={`tel:${l.phone}`} className="text-[#8B1538] hover:text-[#6A0F2A]" title="Llamar"><Phone size={13}/></a>
                    <a onClick={(e)=>e.stopPropagation()} href={waLink(l.phone)} target="_blank" rel="noreferrer" className="text-emerald-600 hover:text-emerald-700" title="WhatsApp"><MessageSquare size={13}/></a>
                  </div>
                </div>
              </article>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function LeadsTable({ leads, onOpen, onUpdateStatus }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="bg-[#F9F8FA] border-b border-[#E3DDE8] text-[#5C5366]">
          <tr>
            <th className="text-left font-bold uppercase tracking-wider text-[11px] px-5 py-3">Fecha</th>
            <th className="text-left font-bold uppercase tracking-wider text-[11px] px-5 py-3">Nombre</th>
            <th className="text-left font-bold uppercase tracking-wider text-[11px] px-5 py-3">Email</th>
            <th className="text-left font-bold uppercase tracking-wider text-[11px] px-5 py-3">Teléfono</th>
            <th className="text-left font-bold uppercase tracking-wider text-[11px] px-5 py-3">Medida</th>
            <th className="text-left font-bold uppercase tracking-wider text-[11px] px-5 py-3">Estado</th>
            <th className="text-left font-bold uppercase tracking-wider text-[11px] px-5 py-3"></th>
          </tr>
        </thead>
        <tbody>
          {leads.map((l) => (
            <tr key={l.id} data-testid={`lead-row-${l.id}`} className="border-b border-[#E3DDE8] hover:bg-[#F9F8FA]/60">
              <td className="px-5 py-4 text-[#5C5366] whitespace-nowrap">{formatDate(l.created_at)}</td>
              <td className="px-5 py-4 font-semibold text-[#2E2638]">{l.name}</td>
              <td className="px-5 py-4"><a className="text-[#8B1538] hover:underline" href={`mailto:${l.email}`}>{l.email}</a></td>
              <td className="px-5 py-4"><a className="text-[#2E2638] hover:underline" href={`tel:${l.phone}`}>{l.phone}</a></td>
              <td className="px-5 py-4 text-[#5C5366]">{l.size_pref || "—"}</td>
              <td className="px-5 py-4">
                <select
                  data-testid={`lead-status-${l.id}`}
                  value={l.status || "nuevo"}
                  onChange={(e) => onUpdateStatus(l.id, e.target.value)}
                  className={`text-xs uppercase tracking-wider font-bold px-2.5 py-1.5 border-0 cursor-pointer ${STATUS_OPTIONS.find((s)=>s.value===(l.status||"nuevo"))?.color || "bg-[#2E2638] text-white"}`}
                >
                  {STATUS_OPTIONS.map((s) => <option key={s.value} value={s.value} className="bg-white text-[#2E2638]">{s.label}</option>)}
                </select>
              </td>
              <td className="px-5 py-4">
                <button
                  data-testid={`open-lead-${l.id}`}
                  onClick={() => onOpen(l)}
                  className="text-xs font-bold uppercase tracking-wider text-[#8B1538] hover:underline"
                >
                  Ver ficha
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function LeadDetailModal({ leadId, onClose, onStatusChanged, onDeleted }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [noteText, setNoteText] = useState("");
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const fetchDetail = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/leads/${leadId}`);
      setData(res.data);
    } catch {
      toast.error("No se pudo cargar la ficha");
    } finally {
      setLoading(false);
    }
  }, [leadId]);

  useEffect(() => { fetchDetail(); }, [fetchDetail]);

  const submitNote = async (e) => {
    e.preventDefault();
    if (!noteText.trim()) return;
    setSaving(true);
    try {
      const res = await axios.post(`${API}/admin/leads/${leadId}/notes`, { text: noteText.trim() });
      setData((d) => ({ ...d, notes: [res.data, ...(d?.notes || [])] }));
      setNoteText("");
      toast.success("Nota añadida");
    } catch {
      toast.error("Error al guardar la nota");
    } finally {
      setSaving(false);
    }
  };

  const changeStatus = async (status) => {
    try {
      await axios.patch(`${API}/admin/leads/${leadId}/status`, { status });
      setData((d) => ({ ...d, lead: { ...d.lead, status } }));
      onStatusChanged(leadId, status);
      // refetch to pull system note
      const res = await axios.get(`${API}/admin/leads/${leadId}`);
      setData(res.data);
      toast.success("Estado actualizado");
    } catch {
      toast.error("Error al actualizar");
    }
  };

  const deleteLead = async () => {
    if (!data?.lead) return;
    const confirmMsg = `¿Seguro que quieres BORRAR la ficha de ${data.lead.name}?\n\nEsta acción es irreversible y también borrará todas sus notas.`;
    if (!window.confirm(confirmMsg)) return;
    setDeleting(true);
    try {
      await axios.delete(`${API}/admin/leads/${leadId}`);
      toast.success("Ficha eliminada");
      onDeleted(leadId);
    } catch {
      toast.error("Error al eliminar la ficha");
      setDeleting(false);
    }
  };

  return (
    <div data-testid="lead-detail-modal" className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-stretch sm:items-center justify-center p-0 sm:p-6" onClick={onClose}>
      <div className="bg-white w-full sm:max-w-2xl max-h-[100vh] sm:max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        {loading || !data ? (
          <div className="p-12 text-center text-[#5C5366]">Cargando ficha…</div>
        ) : (
          <>
            <div className="sticky top-0 bg-white border-b border-[#E3DDE8] px-6 py-4 flex items-center justify-between">
              <div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-[#8B1538] font-bold">Ficha del lead</div>
                <h3 className="font-display text-2xl font-bold text-[#2E2638] leading-tight">{data.lead.name}</h3>
              </div>
              <button data-testid="close-lead-modal" onClick={onClose} className="p-2 hover:bg-[#F9F8FA] text-[#5C5366]"><X size={18}/></button>
            </div>

            <div className="p-6 space-y-6">
              {/* Info + acciones rápidas */}
              <div className="grid sm:grid-cols-2 gap-3">
                <a href={`tel:${data.lead.phone}`} className="flex items-center gap-3 p-3 border border-[#E3DDE8] hover:border-[#8B1538] hover:bg-[#F9F8FA] transition-colors">
                  <div className="w-10 h-10 bg-[#8B1538] text-white flex items-center justify-center"><Phone size={16}/></div>
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-[#5C5366] font-bold">Llamar</div>
                    <div className="font-bold text-[#2E2638]">{data.lead.phone}</div>
                  </div>
                </a>
                <a href={waLink(data.lead.phone)} target="_blank" rel="noreferrer" className="flex items-center gap-3 p-3 border border-[#E3DDE8] hover:border-emerald-600 hover:bg-[#F9F8FA] transition-colors">
                  <div className="w-10 h-10 bg-emerald-600 text-white flex items-center justify-center"><MessageSquare size={16}/></div>
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-[#5C5366] font-bold">WhatsApp</div>
                    <div className="font-bold text-[#2E2638]">Abrir chat</div>
                  </div>
                </a>
                <a href={`mailto:${data.lead.email}`} className="flex items-center gap-3 p-3 border border-[#E3DDE8] hover:border-[#2E2638] hover:bg-[#F9F8FA] transition-colors sm:col-span-2">
                  <div className="w-10 h-10 bg-[#2E2638] text-white flex items-center justify-center"><Mail size={16}/></div>
                  <div className="min-w-0">
                    <div className="text-[10px] uppercase tracking-wider text-[#5C5366] font-bold">Email</div>
                    <div className="font-bold text-[#2E2638] truncate">{data.lead.email}</div>
                  </div>
                </a>
              </div>

              {/* Datos */}
              <dl className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <dt className="text-[10px] uppercase tracking-wider text-[#5C5366] font-bold">Recibido</dt>
                  <dd className="text-[#2E2638]">{formatDate(data.lead.created_at)}</dd>
                </div>
                <div>
                  <dt className="text-[10px] uppercase tracking-wider text-[#5C5366] font-bold">Medida</dt>
                  <dd className="text-[#2E2638]">{data.lead.size_pref || "—"}</dd>
                </div>
              </dl>

              {/* Estado */}
              <div>
                <div className="text-[10px] uppercase tracking-wider text-[#5C5366] font-bold mb-2">Estado del lead</div>
                <div className="flex flex-wrap gap-2">
                  {STATUS_OPTIONS.map((s) => (
                    <button
                      key={s.value}
                      data-testid={`status-btn-${s.value}`}
                      onClick={() => changeStatus(s.value)}
                      className={`px-3 py-1.5 text-xs uppercase tracking-wider font-bold transition-all ${(data.lead.status || "nuevo") === s.value ? s.color + " ring-2 ring-offset-1 ring-[#2E2638]" : "bg-white border border-[#E3DDE8] text-[#5C5366] hover:border-[#8B1538]"}`}
                    >
                      {s.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Notas */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <div className="text-[10px] uppercase tracking-wider text-[#5C5366] font-bold">Notas y seguimiento</div>
                  <span className="text-xs text-[#B5AFBE]">{data.notes.length} entradas</span>
                </div>
                <form onSubmit={submitNote} className="mb-4 flex gap-2">
                  <textarea
                    data-testid="note-input"
                    value={noteText}
                    onChange={(e) => setNoteText(e.target.value)}
                    placeholder="Anota qué te ha dicho el cliente, cuándo volver a llamar, cita en tienda…"
                    rows={2}
                    className="flex-1 px-3 py-2 border border-[#E3DDE8] focus:border-[#8B1538] outline-none text-sm resize-none"
                  />
                  <button
                    data-testid="note-submit"
                    type="submit"
                    disabled={saving || !noteText.trim()}
                    className="self-end px-4 py-2 bg-[#8B1538] hover:bg-[#6A0F2A] disabled:bg-[#B5AFBE] text-white text-sm font-bold uppercase tracking-wider flex items-center gap-2 transition-colors"
                  >
                    <Send size={14}/> {saving ? "…" : "Añadir"}
                  </button>
                </form>

                <ul className="space-y-3">
                  {data.notes.length === 0 ? (
                    <li className="text-sm text-[#B5AFBE] text-center py-6">Aún no hay notas. Empieza el seguimiento.</li>
                  ) : data.notes.map((n) => (
                    <li
                      key={n.id}
                      data-testid={`note-${n.id}`}
                      className={`p-3 border-l-2 ${n.kind === "system" ? "bg-[#F9F8FA] border-[#B5AFBE]" : "bg-white border-[#8B1538]"}`}
                    >
                      <div className="flex items-center justify-between mb-1 text-[11px] text-[#5C5366]">
                        <span className="flex items-center gap-1.5 font-bold">
                          <UserIcon size={11}/>
                          {n.author_name || n.author_email || "—"}
                          {n.kind === "system" && <span className="bg-[#B5AFBE] text-white px-1.5 py-0.5 text-[9px] uppercase tracking-wider font-bold">Auto</span>}
                        </span>
                        <span>{formatDate(n.created_at)}</span>
                      </div>
                      <p className="text-sm text-[#2E2638] whitespace-pre-wrap">{n.text}</p>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Danger zone: eliminar ficha */}
              <div className="pt-6 mt-4 border-t border-[#E3DDE8]">
                <div className="text-[10px] uppercase tracking-wider text-[#B5AFBE] font-bold mb-2">Zona peligrosa</div>
                <button
                  data-testid="delete-lead-btn"
                  onClick={deleteLead}
                  disabled={deleting}
                  className="w-full sm:w-auto px-5 py-3 border border-red-300 text-red-700 hover:bg-red-50 disabled:bg-[#F9F8FA] disabled:text-[#B5AFBE] disabled:border-[#E3DDE8] text-xs uppercase tracking-wider font-bold flex items-center justify-center gap-2 transition-colors"
                >
                  <Trash2 size={14}/> {deleting ? "Eliminando…" : "Eliminar ficha de lead"}
                </button>
                <p className="mt-2 text-xs text-[#B5AFBE]">Se borrarán también todas las notas asociadas. Esta acción no se puede deshacer.</p>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
